import { FormEvent, ReactNode } from 'react';
import { FieldValues, FormProvider as RHFForm, UseFormReturn } from 'react-hook-form';

// ----------------------------------------------------------------------

// The three generics mirror `UseFormReturn`'s own, in this component's order:
//
// - `TFieldValues` — the form's field values, as always.
// - `TTransformedValues` — the resolver's output type, which `UseFormReturn` keeps
//   separate from the field values. Naming it here rather than letting it default to
//   `TFieldValues` is what lets a `useForm` whose resolver reports a different output
//   type be passed in at all — the case for every form whose Yup schema still lives in
//   an untyped `.js` hook, where the resolver infers an unrelated shape and the two
//   positions can never coincide.
// - `TContext` — `useForm`'s `context` option, which nothing in this component reads.
//   It defaults to `unknown` rather than react-hook-form's own `any`, but stays a real
//   parameter instead of being pinned to `unknown` in the `methods` position: the type
//   sits in a contravariant slot inside `Control._options.resolver`, so a pinned
//   `unknown` would put this shared component's own signature between a
//   `useForm<Values, MyCtx>({ context })` caller and its `<Form>`. Inference reaches
//   `TContext` through `UseFormReturn.control`, so no call site has to spell it out.
interface FormProps<
  TFieldValues extends FieldValues = FieldValues,
  TTransformedValues = TFieldValues,
  TContext = unknown
> {
  children: ReactNode;
  onSubmit: (data: TTransformedValues) => void;
  methods: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  // Opt-in for forms nested inside another form (e.g. a dialog rendered through a
  // portal inside a parent form): stops the synthetic submit event from bubbling up
  // the React tree and triggering the parent form's onSubmit.
  stopPropagation?: boolean;
}

export function Form<
  TFieldValues extends FieldValues = FieldValues,
  TTransformedValues = TFieldValues,
  TContext = unknown
>({
  children,
  onSubmit,
  methods,
  stopPropagation = false,
}: FormProps<TFieldValues, TTransformedValues, TContext>) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (stopPropagation) event.stopPropagation();
    void methods.handleSubmit(onSubmit)(event);
  };

  return (
    <RHFForm {...methods}>
      <form noValidate autoComplete="off" onSubmit={handleSubmit}>
        {children}
      </form>
    </RHFForm>
  );
}
